import { useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { components } from '../../api/generated/schema'
import type { AsyncState } from '../async/Async'
import { fromQuery } from '../async/fromQuery'

/**
 * The icon catalogue — `/api/v1/category-icons`, backend PR #402, contract in
 * the backend's `docs/frontend-integration/category-taxonomy.md` §3a (#447).
 *
 * 1,518 Phosphor duotone icons in 53 groups. `POST`/`PATCH /categories` AND
 * `POST`/`PATCH /merchants` (backend #403/#404) accept `icon` ONLY if it is
 * one of these slugs and 422 anything else, so the picker offers exactly what
 * these endpoints return and never builds a slug of its own.
 *
 * **It lives in `src/platform/` because it has two callers — #461.** The
 * endpoint is named for categories and the allowlist is not: the merchant
 * write paths validate against the same 1,518 slugs. A hook under
 * `routes/categories/` would have made the merchant form read a sibling
 * route's internals, which is the boundary this repo does not cross.
 *
 * **Caching.** Every response carries a strong `ETag` and
 * `Cache-Control: private, max-age=300`. The browser's HTTP cache honours
 * both for `fetch` on its own — it serves from cache inside five minutes and
 * revalidates with `If-None-Match` after — so nothing here re-implements
 * that. `staleTime` matches the five minutes, so reopening the picker does
 * not refetch what TanStack already holds.
 */

export type CatalogueIcon = components['schemas']['CategoryIconResponse']
export type IconGroup = components['schemas']['CategoryIconGroupResponse']
type IconPageResponse = components['schemas']['CategoryIconList']

/** The server's `q` limit, checked on the RAW query before it trims. */
export const ICON_QUERY_MAX = 50

/** One page of the grid. Within the server's 1–100; about seven rows of a
 *  dialog-width grid. */
export const ICON_PAGE_SIZE = 60

const FIVE_MINUTES = 5 * 60 * 1000

/**
 * What a catalogue request throws — the shape `CategoriesError` has, without
 * the import. Nothing in `src/platform/**` may reach into a route module, and
 * the failure is the catalogue's, not any one caller's.
 *
 * Carries the HTTP `status` as well as the parsed body: `openapi-fetch`'s
 * `error` IS the body and has no status of its own.
 */
export class IconCatalogueError extends Error {
  readonly status?: number

  constructor(cause: unknown, status?: number) {
    super('icon-catalogue-unavailable', { cause })
    this.name = 'IconCatalogueError'
    this.status = status
  }
}

async function listGroups(): Promise<IconGroup[]> {
  const { data, error, response } = await api.GET('/api/v1/category-icons/groups')
  if (error !== undefined || data === undefined) throw new IconCatalogueError(error, response.status)
  return data.groups
}

/**
 * The group tabs. Four states: the catalogue has no upstream sync
 * (`category-icons` is `live`), and an empty list means the endpoint is
 * broken, not that there are no icons — but there is no page to show without
 * a group, so empty is still reported rather than rendered as success.
 */
export function useIconGroups(): AsyncState<IconGroup[]> {
  const query = useQuery({
    queryKey: keys.categoryIcons.groups(),
    queryFn: listGroups,
    staleTime: FIVE_MINUTES,
  })
  return fromQuery(query, {
    message: () => 'Icon groups could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (groups) => groups.length === 0,
  })
}

/** What the grid renders: every page loaded so far, in order. */
export type IconPage = { icons: CatalogueIcon[]; total: number }

/** Hoisted, so TanStack can memoise it by identity. */
const flatten = (data: InfiniteData<IconPageResponse, number>): IconPage => ({
  icons: data.pages.flatMap((p) => p.icons),
  total: data.pages[0]?.total ?? 0,
})

/**
 * The term actually sent, or `null` for "browse instead".
 *
 * **Blank is never sent.** The server 422s a `q` that is empty after
 * trimming, so a blank or whitespace-only query browses. Cut to
 * `ICON_QUERY_MAX` as well: the input caps it too, but a hook that trusts its
 * caller to have capped it would send a 422 the day a second caller forgets.
 */
function searchTerm(query: string): string | null {
  const term = query.trim().slice(0, ICON_QUERY_MAX).trim()
  return term === '' ? null : term
}

/**
 * Icons for the picker: a search when there is a query, otherwise a browse of
 * `group` (`null` = every group, in the server's group order).
 *
 * **Search spans every group** and ignores `group` on purpose. The picker's
 * search field is how a user finds an icon they cannot place in a group, so
 * limiting it to whichever tab happens to be selected would hide it.
 *
 * **Paged, not truncated.** Search ranking is weak on typos ("gitar" ranks
 * guitar 10th), so every page stays reachable through `loadMore`, and the
 * pages accumulate rather than replace one another.
 *
 * The caller debounces `query`; this hook sends whatever it is given.
 */
export function useCatalogueIcons({ query, group }: { query: string; group: string | null }): {
  state: AsyncState<IconPage>
  hasMore: boolean
  loadingMore: boolean
  loadMore: () => void
} {
  const term = searchTerm(query)
  const result = useInfiniteQuery({
    queryKey: term === null ? keys.categoryIcons.browse(group) : keys.categoryIcons.search(term),
    queryFn: async ({ pageParam }): Promise<IconPageResponse> => {
      if (term !== null) {
        const { data, error, response } = await api.GET('/api/v1/category-icons/search', {
          params: { query: { q: term, page: pageParam, page_size: ICON_PAGE_SIZE } },
        })
        if (error !== undefined || data === undefined) throw new IconCatalogueError(error, response.status)
        return data
      }
      const { data, error, response } = await api.GET('/api/v1/category-icons', {
        params: {
          // Omitted, not `null` or `''`, for every group: the server treats
          // an unknown group as an empty page, not as "all".
          query: { ...(group === null ? {} : { group }), page: pageParam, page_size: ICON_PAGE_SIZE },
        },
      })
      if (error !== undefined || data === undefined) throw new IconCatalogueError(error, response.status)
      return data
    },
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
    select: flatten,
    staleTime: FIVE_MINUTES,
  })

  const state = fromQuery(result, {
    message: () => (term === null ? 'Icons could not be loaded.' : 'The icon search failed.'),
    recovery: { label: 'Try again', onAct: () => void result.refetch() },
    isEmpty: (data) => data.icons.length === 0,
  })

  return {
    state,
    hasMore: result.hasNextPage,
    loadingMore: result.isFetchingNextPage,
    loadMore: () => {
      // Guarded here rather than trusted to the button: a second call while
      // one page is in flight would request it twice.
      if (result.hasNextPage && !result.isFetchingNextPage) void result.fetchNextPage()
    },
  }
}
